import { apiRequest } from '@/lib/api-request';

export interface UsageTotals {
  calls: number;
  input_tokens: number;
  cached_input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  weighted_usd: number;
  /** admins only */
  provider_cost_usd?: number;
  customer_amount?: number;
}

export interface UserBudget {
  /** an admin set limits for this user (otherwise the defaults apply: $10 a day, no monthly cap) */
  has_budget: boolean;
  /** null = no monthly cap */
  monthly_cap_usd: number | null;
  month_used_usd?: number;
  remaining_usd?: number | null;
  month_percent?: number | null;
  daily_limit_usd: number;
  day_used_usd?: number;
  day_percent?: number | null;
  blocked?: boolean;
}

export type MyUsage =
  | { available: false; estimate: true; time_zone: string; message: string }
  | { available: true; estimate: true; time_zone: string; day: UsageTotals; month: UsageTotals; models: Array<UsageTotals & { model: string }>; budget: UserBudget };

export interface TeamUsageUser extends UsageTotals {
  user_id: string;
  name: string;
  role: string;
  /** null = no monthly cap */
  monthly_cap_usd: number | null;
  daily_limit_usd: number;
  has_custom_limits: boolean;
  /** today's consumption (from midnight in the company's time zone) */
  today_weighted_usd: number;
  month_percent: number | null;
}

export type CompanyUsage =
  | { available: false; estimate: true; time_zone: string; message: string }
  | { available: true; estimate: true; time_zone: string; period_start: string; total: UsageTotals; users: TeamUsageUser[]; models: Array<UsageTotals & { model: string }> };

export interface BobSettings {
  available: boolean;
  timezone: string | null;
  timezone_confirmed: boolean;
  /** the zone the review is read in: the company's, or UTC while it has not confirmed one */
  review_timezone: string;
  review_hour: number;
  review_slot_label: string;
  followup_after_hours: number;
  next_review_at: string | null;
  scheduler: { running: boolean; message: string; last_tick_at?: string | null };
  saved: boolean;
}

export const fetchMyUsage = (signal?: AbortSignal) => apiRequest<MyUsage>('api/ai-usage/me', { signal });
export const fetchCompanyUsage = (signal?: AbortSignal) => apiRequest<CompanyUsage>('api/ai-usage/company', { signal });
export const saveUserBudget = (userId: string, body: { monthly_cap_usd: number | null; daily_limit_usd: number }) =>
  apiRequest<CompanyUsage>(`api/ai-usage/budgets/${userId}`, { method: 'PUT', body });
export const clearUserBudget = (userId: string) => apiRequest<CompanyUsage>(`api/ai-usage/budgets/${userId}`, { method: 'DELETE' });
export const fetchBobSettings = (signal?: AbortSignal) => apiRequest<BobSettings>('api/bob/settings', { signal });
export const saveBobSettings = (body: Partial<Pick<BobSettings, 'review_hour' | 'followup_after_hours'>>) =>
  apiRequest<BobSettings>('api/bob/settings', { method: 'PUT', body });

export const formatUsd = (value: number | null | undefined, digits = 2) =>
  value === null || value === undefined || Number.isNaN(value) ? '—' : `$${value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
export const formatTokens = (value: number) => value.toLocaleString('en-US');
